/**
 * use-feedback — feedback API hooks (ADM-FBK-S001~S004)
 */

import type {
  CreateFeedbackAttachmentRequest,
  CreateFeedbackAttachmentResponse,
  FeedbackDetailResponse,
  FeedbackListItem,
  ReplyFeedbackRequest,
} from '@novel-admin/shared';
import { type OffsetPagedResult, apiRequest, apiRequestOffsetPaged } from '~/api/client';

export const feedbackApi = {
  // ADM-FBK-S001 offset 分页（admin 列表默认 offset，dev-docs 40-分页与排序）：envelope.data=裸数组 +
  // total/page/limit/hasMore 顶层（okOffsetPaged）。useFeedback 收件箱按 page 递增累积全量（tab 计数 / 客户端
  // 筛选 / master-detail 需全集），避免靠后 OPEN 工单从列表 + 待回复 count 双双消失（adversarial F7）。
  listOffsetPaged: (
    status?: string,
    page?: number,
    appId?: string,
    limit?: number,
  ): Promise<OffsetPagedResult<FeedbackListItem>> =>
    apiRequestOffsetPaged<FeedbackListItem>({
      method: 'GET',
      path: '/api/v1/feedback',
      // ADM-FBK-S001：app_id 维度筛选（顶栏 app-scope，'all' 由调用方转 undefined 省略）
      query: {
        ...(status ? { status } : {}),
        ...(page ? { page } : {}),
        ...(limit ? { limit } : {}),
        ...(appId ? { app_id: appId } : {}),
      },
    }),
  // 仅取首页数组（不关心分页的内部调用复用）；翻页累积请用 listOffsetPaged
  list: (status?: string) =>
    apiRequest<FeedbackListItem[]>({
      method: 'GET',
      path: '/api/v1/feedback',
      query: { ...(status ? { status } : {}) },
    }),
  detail: (id: string) =>
    apiRequest<FeedbackDetailResponse>({ method: 'GET', path: `/api/v1/feedback/${id}` }),
  reply: (id: string, body: ReplyFeedbackRequest) =>
    apiRequest<{ ok: true }>({
      method: 'POST',
      path: `/api/v1/feedback/${id}/reply`,
      body,
    }),
  close: (id: string) =>
    apiRequest<{ ok: true }>({ method: 'POST', path: `/api/v1/feedback/${id}/close` }),
  /**
   * ADM-FBK-S002 附件 pre-create（dev-docs A7 §3.5）：拿 attachment_id + R2 PutObject 签名 URL。
   * 工单 scoped —— 服务端从 feedback.app_id 取 app_id，请求体只带 content_type / size_bytes。
   */
  createAttachment: (id: string, body: CreateFeedbackAttachmentRequest) =>
    apiRequest<CreateFeedbackAttachmentResponse>({
      method: 'POST',
      path: `/api/v1/feedback/${id}/attachments`,
      body,
    }),
};

/**
 * 把文件字节 PUT 到 pre-create 返回的 R2 签名 URL（客户端 PUT 上传口径，dev-docs 80-反馈与客服 §3.5）。
 *
 * **只带 `Content-Type` 一个 header**：presigned 签名只覆盖 `host` + `Content-Type`，多带任何
 * 未签名 header（`x-amz-*` / `Content-Disposition` 等）会触发 R2 `403 SignatureDoesNotMatch`。
 * `Content-Length` 由浏览器按 body 自动设置，不手填。成功判 2xx、不解析响应体。
 *
 * ⚠️ 这里**刻意用裸 fetch**：目标是 R2 的签名 URL、不是 admin API —— 带上 admin `Authorization`
 * 反而会破坏签名。「带鉴权必走 authedFetch」的铁律约束的是 admin API 请求，本调用不在其内。
 */
export async function putAttachmentToSignedUrl(
  signedUploadUrl: string,
  file: File,
  contentType: string,
): Promise<void> {
  const res = await fetch(signedUploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!res.ok) throw new Error(`附件上传失败（R2 返回 ${res.status}）`);
}
